import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { ChatContainerProps, SuggestionItem } from '../../types/chat';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';
import MessageInput from './MessageInput';
import ScrollToBottom from './ScrollToBottom';

interface ExtendedChatContainerProps extends ChatContainerProps {
  onSelectSuggestion?: (suggestion: SuggestionItem) => void;
  onSearchRecommendation?: (query: string) => void;
  startSignal?: number;
}

const ChatContainer: React.FC<ExtendedChatContainerProps> = ({
  messages,
  isTyping,
  onSendMessage,
  onSelectSuggestion,
  onSearchRecommendation,
  className = '',
  startSignal
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const previousMessagesLength = useRef(messages.length);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  }, []);

  // Check if user is near bottom of chat
  const checkScrollPosition = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom);
      setIsUserScrolling(!isNearBottom);
    }
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  // Auto-scroll when new messages arrive - force scroll for new messages
  useEffect(() => {
    const hasNewMessages = messages.length > previousMessagesLength.current;
    
    if (hasNewMessages) {
      // Force scroll to bottom when new messages are added (user message or AI response)
      setIsUserScrolling(false); // Reset user scrolling state
      scrollToBottom();
      previousMessagesLength.current = messages.length;
    } else if (!isUserScrolling) {
      // Normal auto-scroll behavior for other changes (like typing indicator)
      scrollToBottom();
    }
  }, [messages, isTyping, scrollToBottom, isUserScrolling]);

  // Initial scroll to bottom
  useEffect(() => {
    scrollToBottom(false);
    previousMessagesLength.current = messages.length;
  }, [scrollToBottom]);

  // Reset user scrolling state after a delay
  useEffect(() => {
    if (isUserScrolling) {
      const timer = setTimeout(() => {
        checkScrollPosition();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isUserScrolling, checkScrollPosition]);

  const handleScrollToBottom = () => {
    setIsUserScrolling(false);
    scrollToBottom();
  };

  return (
    <div className={`flex flex-col h-full bg-white w-full max-w-full ${className}`}>
      {/* Messages container */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 space-y-4 min-h-0"
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {/* Welcome message */}
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg 
                className="w-8 h-8 text-blue-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Chào mừng đến với Chat
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Bắt đầu cuộc trò chuyện bằng cách nhập tin nhắn bên dưới. Tôi ở đây để giúp trả lời các câu hỏi của bạn!
            </p>
          </div>
        )}

        {/* Chat messages */}
        {messages.map((message) => (
          <ChatMessage 
            key={message.id} 
            message={message}
            showAvatar={true}
            showTimestamp={true}
            onSelectSuggestion={onSelectSuggestion}
            onSearchRecommendation={onSearchRecommendation}
            onScrollToBottom={scrollToBottom}
            startSignal={startSignal && messages.length === 1 ? startSignal : undefined}
          />
        ))}

        {/* Typing indicator */}
        <TypingIndicator isVisible={isTyping} />

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      <ScrollToBottom 
        isVisible={showScrollToBottom}
        onClick={handleScrollToBottom}
      />

      {/* Message input - Fixed at bottom */}
      <div className="flex-shrink-0 border-t bg-white">
        <MessageInput 
          onSendMessage={onSendMessage}
          disabled={isTyping}
          placeholder="Nhập tin nhắn của bạn..."
          maxCharacters={500}
        />
      </div>
    </div>
  );
};

export default ChatContainer;
